import { FormEvent, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { Check } from "lucide-react";

import {
  createTenant,
  getTenant,
  listDeployments,
  putStage,
  triggerDeploy,
  type PutStageResponse,
} from "@/api/tenants";
import { DeploymentTimeline } from "@/components/DeploymentTimeline";
import { Checkbox, Field, Fieldset, Input } from "@/components/form";
import { LaunchStackPanel } from "@/components/LaunchStackPanel";
import { ErrorBox, MutationError, Spinner } from "@/components/Spinner";
import { DeploymentStatusBadge, ReadinessBadge } from "@/components/StatusBadge";
import {
  POLL_INTERVAL_MS,
  inflightCount,
  latestDeploymentByStage,
} from "@/lib/deployments";
import { Button, ButtonLink, FormActionBar, Icon, Notice, Page, PageHeader } from "@/components/ui";
import { useDirtyValues } from "@/hooks/useDirtyValues";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";

type Step = "identity" | "environment" | "claim" | "deploy";

const STEPS: { key: Step; label: string }[] = [
  { key: "identity", label: "Tenant" },
  { key: "environment", label: "Environment" },
  { key: "claim", label: "Installation" },
  { key: "deploy", label: "Deployment" },
];

const PULSESTATION_ROOT = "pulsestation.io";

// Mirror of the backend's ``slugify_tenant_id`` (``models.py``): NFKD, then every character that is
// not ASCII is DROPPED (``encode("ascii", "ignore")``), lowercase, each run of anything but letters
// and digits becomes one hyphen, the hyphens at the ends go, at most 32 characters. The backend is
// authoritative; this is what fills the Identifier field so the operator SEES the address before
// anything exists. Dropping (not turning into a hyphen) is what keeps it in step with the backend on
// a letter with no decomposition ("Straße" → "strae", as the server would have written it).
function slugifyTenantId(displayName: string): string {
  return displayName
    .normalize("NFKD")
    .replace(/[^\u0000-\u007f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32)
    .replace(/-+$/, "");
}

// The identifier fixes the tenant's address and can never be changed: it is checked here against
// the very rule the backend applies (``_TENANT_ID_RE``: 1 to 32 characters, lowercase letters,
// digits and hyphens, never a hyphen at either end), so a refusal is read before the button is
// pressed and not after. The hyphen is escaped: an unescaped one at the end of a class is a syntax
// error under the ``v`` flag the browsers compile ``pattern`` with, and the whole rule is then ignored.
const IDENTIFIER_PATTERN = "[a-z0-9]([a-z0-9\\-]{0,30}[a-z0-9])?";

export function OnboardingWizardPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("identity");
  const [tenantId, setTenantId] = useState<string>("");
  const [displayName, setDisplayName] = useState("");
  // The identifier follows the display name until the operator types in it (clearing it hands it back).
  const [identifier, setIdentifier] = useState("");
  const [identifierTouched, setIdentifierTouched] = useState(false);
  const [costEstimationUi, setCostEstimationUi] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminGiven, setAdminGiven] = useState("");
  const [adminFamily, setAdminFamily] = useState("");

  const [stageName, setStageName] = useState("dev");
  const [isProduction, setIsProduction] = useState(false);
  const [launch, setLaunch] = useState<{ url: string; expiresAt: number } | null>(
    null,
  );

  // The first step is a form the operator fills in: what they typed there is lost if they leave. From
  // the second step on the tenant EXISTS and what is left to fill in is a name with its default and a
  // checkbox, so nothing is held there. The form is open from the first render, so its clean values
  // are the empty ones.
  const { dirty } = useDirtyValues(
    { displayName, identifier, adminGiven, adminFamily, adminEmail, costEstimationUi, mfaRequired },
    step === "identity",
  );
  useUnsavedChanges(dirty);

  // --- Step 1: create the tenant -------------------------------------------
  // Creating a tenant cannot be undone, and a second identical request is a second tenant or a
  // confusing 409: the submission is guarded against RE-ENTRY. ``isPending`` alone is not enough — it
  // only turns true on the next render, so two submits in the same tick both see ``false`` — hence a
  // ref, set synchronously by the first call and cleared when the mutation settles (done or failed:
  // after a refusal the operator corrects and submits again).
  const creating = useRef(false);
  const createMut = useMutation({
    mutationFn: () =>
      createTenant(token, {
        tenant_id: identifier,
        display_name: displayName,
        admin_email: adminEmail,
        admin_given_name: adminGiven,
        admin_family_name: adminFamily,
        cost_estimation_ui_enabled: costEstimationUi,
        mfa_required: mfaRequired,
      }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      setTenantId(created.tenantId);
      setStep("environment");
    },
    onSettled: () => {
      creating.current = false;
    },
  });
  function submitTenant() {
    if (creating.current || createMut.isPending) return;
    creating.current = true;
    createMut.mutate();
  }

  // --- Step 2: create the first environment --------------------------------
  const stageMut = useMutation({
    mutationFn: () =>
      putStage(token, tenantId, stageName, { is_production: isProduction }),
    onSuccess: (res: PutStageResponse) => {
      qc.invalidateQueries({ queryKey: ["tenant", tenantId] });
      if (res.launchStackUrl && res.expiresAt) {
        setLaunch({ url: res.launchStackUrl, expiresAt: res.expiresAt });
      }
      setStep("claim");
    },
  });

  // --- Step 3: wait for the customer to claim (poll the tenant) ------------
  const claimQuery = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => getTenant(token, tenantId),
    enabled: !!token && !!tenantId && (step === "claim" || step === "deploy"),
    refetchInterval: (q) => {
      if (step !== "claim") return false;
      const s = q.state.data?.stages.find((x) => x.stage === stageName);
      const claimed = !!s?.environmentClaimed && !!s?.loginReady;
      return claimed ? false : POLL_INTERVAL_MS;
    },
    refetchIntervalInBackground: false,
  });
  const currentStage = claimQuery.data?.stages.find((s) => s.stage === stageName);
  const claimed = !!currentStage?.environmentClaimed && !!currentStage?.loginReady;

  // --- Step 4: trigger + watch the first deployment ------------------------
  const deployMut = useMutation({
    mutationFn: () => triggerDeploy(token, tenantId, stageName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deployments", tenantId] });
    },
  });
  const deploymentsQuery = useQuery({
    queryKey: ["deployments", tenantId],
    queryFn: () => listDeployments(token, tenantId, { limit: 10 }),
    enabled: !!token && !!tenantId && step === "deploy",
    refetchInterval: (q) => {
      const items = q.state.data?.items ?? [];
      if (inflightCount(items) > 0) return POLL_INTERVAL_MS;
      // Keep polling for a moment after the trigger until the freshly-created
      // run shows up, otherwise an empty list would stop the poll and strand
      // the "récupération…" spinner.
      if (
        deployMut.isSuccess &&
        !latestDeploymentByStage(items).get(stageName)
      ) {
        return POLL_INTERVAL_MS;
      }
      return false;
    },
    refetchIntervalInBackground: false,
  });
  const latestRun = latestDeploymentByStage(
    deploymentsQuery.data?.items ?? [],
  ).get(stageName);

  // Non-prod = hyphenated sibling `{stage}-{tenant}` (single DNS label),
  // mirrors compute_stage_domain — see StageForm for the rationale.
  const urlHost = isProduction
    ? `${tenantId}.${PULSESTATION_ROOT}`
    : `${stageName || "<environment>"}-${tenantId}.${PULSESTATION_ROOT}`;

  const activeIndex = STEPS.findIndex((s) => s.key === step);

  return (
    <Page width="form">
      <PageHeader
        back={{ to: "/tenants", label: "Tenants" }}
        title="Onboard a new tenant"
        subtitle="Four steps, from creating the tenant to the first deployment."
      />

      <div className="space-y-8">
        <ProgressBar activeIndex={activeIndex} />

        {step === "identity" && (
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              submitTenant();
            }}
          >
            <div className="card space-y-5">
              <Field label="Display name">
                <Input
                  value={displayName}
                  onChange={(e) => {
                    const next = e.target.value;
                    setDisplayName(next);
                    // Mirror the slug into the identifier as long as the operator has not typed in it.
                    if (!identifierTouched) setIdentifier(slugifyTenantId(next));
                  }}
                  required
                  autoFocus
                />
              </Field>
              <Field
                label="Identifier"
                help="Pre-filled from the display name. Lowercase letters, digits and hyphens. It becomes the tenant's address and cannot be changed afterwards."
              >
                <Input
                  mono
                  suffix=".pulsestation.io"
                  value={identifier}
                  onChange={(e) => {
                    const next = e.target.value;
                    // Clearing the field is the natural "give me the suggestion back" gesture:
                    // resume mirroring from the display name.
                    setIdentifierTouched(next !== "");
                    setIdentifier(next === "" ? slugifyTenantId(displayName) : next);
                  }}
                  required
                  maxLength={32}
                  pattern={IDENTIFIER_PATTERN}
                  title="1 to 32 characters: lowercase letters, digits and hyphens, and not starting or ending with a hyphen."
                />
              </Field>
              <Fieldset
                legend="Admin contact"
                help="This person receives an email invitation and becomes the first administrator of the tenant's workspace."
              >
                <div className="mt-3 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="First name">
                      <Input
                        value={adminGiven}
                        onChange={(e) => setAdminGiven(e.target.value)}
                        required
                        maxLength={64}
                      />
                    </Field>
                    <Field label="Last name">
                      <Input
                        value={adminFamily}
                        onChange={(e) => setAdminFamily(e.target.value)}
                        required
                        maxLength={64}
                      />
                    </Field>
                  </div>
                  <Field label="Email">
                    <Input
                      type="email"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      required
                      maxLength={254}
                    />
                  </Field>
                </div>
              </Fieldset>
              <Fieldset legend="Options" className="border-t border-line pt-4">
                <div className="mt-2 space-y-2">
                  <Checkbox
                    label={<span className="font-medium">Cost estimation display</span>}
                    description={
                      <>
                        Shows the "Estimated Cost" card in the tenant's GUI. The
                        calculation and the API always stay active; this setting
                        only controls the display. Takes effect on the tenant's next
                        deployment.
                      </>
                    }
                    checked={costEstimationUi}
                    onChange={(e) => setCostEstimationUi(e.target.checked)}
                  />
                  <Checkbox
                    label={<span className="font-medium">Require two-factor authentication</span>}
                    description={
                      <>
                        Applies to every stage of this tenant. Users without a second
                        factor will be asked to set one up at their next sign-in.
                      </>
                    }
                    checked={mfaRequired}
                    onChange={(e) => setMfaRequired(e.target.checked)}
                  />
                  {/* A sibling of the checkbox, not inside its description (a <span>): a message
                      box is a block, and a bare tinted span carried its meaning by colour alone. */}
                  {mfaRequired && (
                    <Notice tone="warning">
                      The appliance local GUI cannot yet answer a second-factor
                      challenge: an operator with one armed is locked out of it.
                      Do not turn this on for a tenant with appliances in service
                      until the gateway image ships that support.
                    </Notice>
                  )}
                </div>
              </Fieldset>
            </div>

            <FormActionBar
              dirty={dirty}
              submitLabel="Continue"
              pendingLabel="Creating…"
              pending={createMut.isPending}
              onCancel={() => navigate("/")}
              error={<MutationError mutation={createMut} title="Could not create the tenant" />}
            />
          </form>
        )}

        {step === "environment" && (
          <form
            className="card space-y-5"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              stageMut.mutate();
            }}
          >
            <MutationError mutation={stageMut} title="Could not create the environment" />
            <p className="text-sm text-ink-muted">
              Tenant <strong>{displayName}</strong> created. Set up its first
              environment.
            </p>
            <Field label="Environment name">
              <Input
                mono
                value={stageName}
                onChange={(e) => setStageName(e.target.value)}
                required
                pattern="[a-z][a-z0-9]{0,31}"
                title="Lowercase letters and digits, starting with a letter."
              />
            </Field>
            <Checkbox
              label="Production environment"
              checked={isProduction}
              onChange={(e) => setIsProduction(e.target.checked)}
            />
            <p className="text-xs text-ink-muted">
              URL: <code className="font-mono">https://{urlHost}</code>
            </p>
            <div className="flex justify-end gap-2 border-t border-line pt-2">
              <Button
                type="submit"
                variant="primary"
                loading={stageMut.isPending}
              >
                {stageMut.isPending ? "Creating…" : "Continue"}
              </Button>
            </div>
          </form>
        )}

        {step === "claim" && (
          <div className="card space-y-4">
            <p className="text-sm text-ink-muted">
              Send the installation link to the tenant's cloud operator. This
              page updates automatically as soon as the environment is claimed.
            </p>
            {launch && (
              <LaunchStackPanel url={launch.url} expiresAt={launch.expiresAt} />
            )}
            <div className="flex items-center gap-2 rounded-md border border-line bg-sunken px-3 py-2 text-sm">
              <ReadinessBadge
                ready={claimed}
                readyLabel="Claimed"
                pendingLabel="Waiting for claim…"
                pulse
              />
            </div>
            {claimQuery.error && <ErrorBox error={claimQuery.error} />}
            <div className="flex justify-end gap-2 border-t border-line pt-2">
              <ButtonLink to={`/tenants/${tenantId}`} variant="ghost">
                Continue later
              </ButtonLink>
              <Button
                variant="primary"
                disabled={!claimed}
                onClick={() => setStep("deploy")}
              >
                Deploy
              </Button>
            </div>
          </div>
        )}

        {step === "deploy" && (
          <div className="card space-y-4">
            {/* Hide the trigger once a deploy has been requested — covers the
                gap between mutation success and the deployments poll surfacing
                the new run, so the operator can't fire a second deploy. */}
            {!latestRun && !deployMut.isPending && !deployMut.isSuccess && (
              <>
                <p className="text-sm text-ink-muted">
                  Everything is ready. Trigger the first deployment of{" "}
                  <strong>{stageName}</strong>.
                </p>
                <MutationError mutation={deployMut} title="Could not start the deployment" />
                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    onClick={() => deployMut.mutate()}
                  >
                    Trigger deployment
                  </Button>
                </div>
              </>
            )}
            {/* Bridge between "deploy requested" and the run surfacing in the
                poll — avoids a blank card with no way forward. */}
            {!latestRun && (deployMut.isPending || deployMut.isSuccess) && (
              <Spinner label="Deployment triggered, fetching progress…" />
            )}
            {latestRun && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-ink">
                    Deployment of {stageName}
                  </p>
                  <DeploymentStatusBadge status={latestRun.status} />
                </div>
                {latestRun.errorMessage && (
                  <Notice tone="danger">
                    <pre className="whitespace-pre-wrap break-words font-mono text-xs">
                      {latestRun.errorMessage}
                    </pre>
                  </Notice>
                )}
                <DeploymentTimeline deployment={latestRun} />
                {latestRun.status === "success" && (
                  // The result of the deployment the operator is waiting on.
                  <Notice tone="success" role="status">
                    Tenant is live
                  </Notice>
                )}
                <div className="flex justify-end border-t border-line pt-2">
                  <ButtonLink to={`/tenants/${tenantId}`} variant="primary">
                    View tenant
                  </ButtonLink>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Page>
  );
}

function ProgressBar({ activeIndex }: { activeIndex: number }) {
  return (
    <ol className="flex items-center gap-2">
      {STEPS.map((s, i) => {
        const done = i < activeIndex;
        const active = i === activeIndex;
        return (
          <li key={s.key} className="flex flex-1 items-center gap-2">
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium ${
                done
                  ? "bg-success-fg text-on-fill"
                  : active
                    ? "bg-brand-500 text-on-fill"
                    : "bg-sunken-deep text-ink-muted"
              }`}
            >
              {done ? <Icon icon={Check} size="sm" label="completed" /> : i + 1}
            </span>
            <span
              className={`text-sm ${active ? "font-medium text-ink" : "text-ink-muted"}`}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 && (
              <span className="h-px flex-1 bg-line" />
            )}
          </li>
        );
      })}
    </ol>
  );
}
